import { buildAppNavItems, type AppNavItem } from "@/config/moduleRegistry";
import { canAccessResource, hasPermissions, isClinicReceptionOnlyUser } from "@/helper/permissionHelper";
import { getPageKeyForRoute } from "@/config/permissionManifest";

function resolveNavAccessKey(item: AppNavItem) {
  if (item.accessKey)
    return item.accessKey;

  const target = item.to as any;
  if (!target)
    return null;

  if (target.path)
    return getPageKeyForRoute(String(target.path));

  if (target.name)
    return getPageKeyForRoute("", String(target.name));

  return null;
}

function filterNav(items: AppNavItem[]): AppNavItem[] {
  return items
    .map((item) => {
      const filteredChildren = item.children ? filterNav(item.children) : undefined;
      const accessKey = resolveNavAccessKey(item);
      const canAccess = accessKey
        ? canAccessResource(accessKey, item.permission)
        : !item.permission || hasPermissions(item.permission);

      if (filteredChildren?.length)
        return { ...item, children: filteredChildren };

      return canAccess ? item : null;
    })
    .filter(Boolean) as AppNavItem[];
}

function collectLeafRoutes(items: AppNavItem[]): any[] {
  return items.flatMap((item) => {
    if (item.children?.length)
      return collectLeafRoutes(item.children);

    return item.to ? [item.to] : [];
  });
}

function isRouteMatch(route: any, matcher: { path?: string; name?: string }) {
  if (!route)
    return false;

  if (matcher.path && route.path === matcher.path)
    return true;

  if (matcher.name && route.name === matcher.name)
    return true;

  return false;
}

export function getDefaultAuthenticatedRoute() {
  // Reception-only staff always land on (and are locked to) the kiosk.
  if (isClinicReceptionOnlyUser())
    return { path: "/clinic-station" };

  const filtered = filterNav(buildAppNavItems());
  const leaves = collectLeafRoutes(filtered);

  const preferredMatchers = [
    { path: "/profile" },
    { path: "/healthcare/visits" },
    { path: "/healthcare/case-sheets" },
    { path: "/pos" },
    { path: "/accounting/sales-invoices" },
    { path: "/warehouse" },
    { name: "pet-profile-list" },
    { name: "root" },
  ];

  for (const matcher of preferredMatchers) {
    const matched = leaves.find(route => isRouteMatch(route, matcher));
    if (matched)
      return matched;
  }

  return leaves[0] ?? { name: "not-authorized" };
}

export default function getNavItems() {
  return filterNav(buildAppNavItems());
}
